import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import html2pdf from 'html2pdf.js';
import '../../../component-styles/Proposal-UI.css';

import HeaderSection from '../Proposals/ProposalForm/HeaderSection';
import { DOCUMENT_TITLE_MAX_LENGTH } from '../../../utils/documentTitle.js';
import InfoSection from '../Proposals/ProposalForm/InfoSection';
import CatalogSection from '../Proposals/ProposalForm/CatalogSection';
import MemoTagsSection from '../Proposals/ProposalForm/MemoTagsSection';
import AttachmentsSection from '../Proposals/ProposalForm/AttachmentsSection';
import TotalsSection from '../Proposals/ProposalForm/TotalsSection';
import InvoiceActions from './InvoiceActions';
import InvoicePayments from './InvoicePayments';
import InvoicePdfDocument from './InvoicePdfDocument';

import LineItemCard from '../Proposals/ProposalForm/LineItems/LineItemCard';
import AddItemForm from '../../../components/ProductsServices/AddItemForm';
import API_BASE from '../../../utils/api.js';
import { unassignedItemsNotice } from '../../../utils/convertToPoMessage.js';
import usePermissions from '../../../auth/usePermissions';
import { authFetch } from '../../utils/authFetch';
import notify from '../../../utils/notify';
import { buildPublicInvoiceUrl } from '../../../utils/publicLinks.js';
import SendEmailModal from '../../common/SendEmailModal';
import ConfirmModal from '../../utils/ConfirmModal';
import {
  getCatalogMarkup,
  getDefaultMarkupByItemType,
  sortRoomsForDesignFlow,
} from '../../../utils/designWorkflow.js';
import {
  autoCreateQuickBooksInvoice,
  autoUpdateQuickBooksInvoice,
  shouldAutoCreateInQuickBooks,
} from '../../../utils/quickbooksAutoSync.js';
import { computeDepositRequestAmount } from '../../../utils/invoiceAdjustments.js';

/* ----------------------------------------------------
   UTILITIES
----------------------------------------------------- */

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v) => Number((toNum(v)).toFixed(2));

const fmt2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

// Form state keeps the deposit value as the raw input string.
const toDepositRequestForm = (depositRequest) => ({
  type: depositRequest?.type === 'amount' ? 'amount' : 'percent',
  value: Number(depositRequest?.value) > 0 ? String(depositRequest.value) : '',
});

const normalizeMarkupValue = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(String(value).replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

/* ----------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------- */

export default function InvoiceForm({ onClose, onSaved, project, initial }) {
  const { permissions } = usePermissions();
  const canEdit = permissions.createEditInvoicesProposals !== false;

  const userId = localStorage.getItem('userId');
  const paymentsSectionRef = useRef(null);
  const pdfRef = useRef(null);

  const [editHeader, setEditHeader] = useState(false);
  const initializedRef = useRef(false);
  const [invoiceStatus, setInvoiceStatus] = useState(initial?.status || 'draft');
  const [sendEmailModal, setSendEmailModal] = useState({ open: false, email: '', publicUrl: '', projectId: '', invoiceId: '' });
  const [convertingPO, setConvertingPO] = useState(false);
  const [confirmPOModal, setConfirmPOModal] = useState(false);
  const [vendorModal, setVendorModal] = useState({ open: false, vendors: [] });
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [itemSelectModal, setItemSelectModal] = useState({ open: false, selected: [] });
  const [pendingConvertItemIds, setPendingConvertItemIds] = useState([]);

  const projectDefaultMarkup = useMemo(
    () => normalizeMarkupValue(project?.defaultMarkup, 25),
    [project?.defaultMarkup]
  );
  const projectDefaultShippingMarkup = useMemo(
    () => normalizeMarkupValue(project?.defaultShippingMarkup, 0),
    [project?.defaultShippingMarkup]
  );
  const defaultMarkupForItem = (item) => ({
    type: '%',
    value: getDefaultMarkupByItemType(item, {
      ...project,
      defaultFfeMarkup: project?.defaultFfeMarkup ?? projectDefaultMarkup,
    }),
  });

  const [taxLocations, setTaxLocations] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [quickbooksSettings, setQuickbooksSettings] = useState(null);
  const [qbConnected, setQbConnected] = useState(false);
  const [defaultShippingDestination, setDefaultShippingDestination] = useState('designer_warehouse');
  const [freightDestinations, setFreightDestinations] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    (async () => {
      try {
        const [settingsRes, qbStatusRes] = await Promise.all([
          fetch(`${API_BASE}/api/settings/${userId}`),
          fetch(`${API_BASE}/api/quickbooks/status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('idToken')}` },
          }),
        ]);
        if (!settingsRes.ok) return;

        const settings = await settingsRes.json();
        setCompanySettings(settings.company || null);
        setInvoiceTemplate(settings.invoiceTemplate || null);
        setQuickbooksSettings(settings?.quickbooks || null);
        setDefaultShippingDestination(settings?.defaultShippingDestination || 'designer_warehouse');
        setFreightDestinations(Array.isArray(settings?.freightDestinations) ? settings.freightDestinations : []);

        if (qbStatusRes.ok) {
          const qbStatus = await qbStatusRes.json().catch(() => ({}));
          setQbConnected(Boolean(qbStatus?.connected));
        }

        const normalized = (settings.taxLocations || []).map((t, idx) => {
          const parsedRate =
            typeof t.tax === 'string'
              ? Number(t.tax.replace('%', '')) / 100
              : Number(t.rate ?? t.tax);
          const rate = Number.isFinite(parsedRate) ? parsedRate : 0;

          return {
            ...t,
            id: t.id || `tax-${idx}`,
            rate,
            tax: rate,
          };
        });

        setTaxLocations(normalized);
      } catch (err) {
        console.error('Failed to load tax settings', err);
      }
    })();
  }, [API_BASE, userId]);

  const resolvedSalesTaxRate = useMemo(() => {
    if (!project?.salesTaxRate) return 0;

    const match = taxLocations.find((t) => t.id === project.salesTaxRate);

    return match?.rate ?? 0;
  }, [project?.salesTaxRate, taxLocations]);

  /* ----------------------------------------------------
     FORM STATE
   ----------------------------------------------------- */

  const initialForm = {
    client: {
      name:
        initial?.client?.name ||
        project?.customerName ||
        `${project?.customerFirst || ''} ${project?.customerLast || ''}`.trim(),
      email: initial?.client?.email || project?.email || '',
    },
    projectId: initial?.projectId ?? project?.id ?? project?.projectId ?? '',
    projectSite:
      initial?.projectSite ||
      (project?.projectStreet
        ? `${project.projectStreet}, ${project.projectCity} ${project.projectState} ${project.projectZip}`
        : ''),
    billingAddress:
      initial?.billingAddress ||
      (project?.billingStreet
        ? `${project.billingStreet}, ${project.billingCity} ${project.billingState} ${project.billingZip}`
        : ''),
    salesTaxRate:
      initial?.salesTaxRate != null ? toNum(initial.salesTaxRate) : resolvedSalesTaxRate,
    notes: initial?.notes || project?.notes || '',
    attachments: initial?.attachments || [],
    invoiceDate: initial?.invoiceDate || new Date().toISOString().split('T')[0],
    dueDate: initial?.dueDate || '',
    title: initial?.title || '',
    depositRequest: toDepositRequestForm(initial?.depositRequest),
  };

  const [form, setForm] = useState(initialForm);
  const [savedInvoice, setSavedInvoice] = useState(initial || null);

  const resolvedTaxLocation = useMemo(() => {
    const snapshotLocationId =
      savedInvoice?.tax?.locationId ??
      initial?.tax?.locationId ??
      savedInvoice?.tax?.location?.id ??
      initial?.tax?.location?.id ??
      project?.salesTaxRate ??
      null;

    if (snapshotLocationId) {
      const matchedById = taxLocations.find((t) => t.id === snapshotLocationId);
      if (matchedById) return matchedById;
    }

    const snapshotRate = savedInvoice?.tax?.rate ?? initial?.tax?.rate ?? form.salesTaxRate;

    if (Number.isFinite(Number(snapshotRate))) {
      return (
        taxLocations.find((t) => Math.abs(Number(t.rate || 0) - Number(snapshotRate)) < 0.0001) ||
        null
      );
    }

    return null;
  }, [
    form.salesTaxRate,
    initial?.tax?.location?.id,
    initial?.tax?.locationId,
    initial?.tax?.rate,
    project?.salesTaxRate,
    savedInvoice?.tax?.location?.id,
    savedInvoice?.tax?.locationId,
    savedInvoice?.tax?.rate,
    taxLocations,
  ]);

  useEffect(() => {
    if (!project) return;
    setForm((prev) => ({
      ...prev,
      client: {
        name:
          project.customerName ||
          `${project.customerFirst || ''} ${project.customerLast || ''}`.trim(),
        email: project.email || prev.client.email,
      },
      projectId: project.id ?? project.projectId ?? prev.projectId,
      projectSite: project.projectStreet
        ? `${project.projectStreet}, ${project.projectCity} ${project.projectState} ${project.projectZip}`
        : prev.projectSite,
      billingAddress: project.billingStreet
        ? `${project.billingStreet}, ${project.billingCity} ${project.billingState} ${project.billingZip}`
        : prev.billingAddress,
      salesTaxRate: resolvedSalesTaxRate,
      notes: project.notes || prev.notes,
    }));
  }, [project, resolvedSalesTaxRate]);

  /* ----------------------------------------------------
     INVOICE NUMBER
  ----------------------------------------------------- */

  const [invoiceNumber, setInvoiceNumber] = useState(initial?.invoiceNumber || '');

  useEffect(() => {
    // Existing invoice → trust backend value
    if (initial?.invoiceNumber) {
      setInvoiceNumber(initial.invoiceNumber);
      return;
    }

    // New invoice → preview next number
    if (!userId || !form.projectId) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/invoices/latest?userId=${userId}&projectId=${form.projectId}`
        );
        const data = await res.json();
        setInvoiceNumber(data?.invoiceNumber || 'IN-00001');
      } catch {
        setInvoiceNumber('IN-00001');
      }
    })();
  }, [API_BASE, userId, form.projectId, initial]);

  /* ----------------------------------------------------
     LINE ITEMS
  ----------------------------------------------------- */

  const [items, setItems] = useState(() =>
    (initial?.lineItems || []).map((it) => ({
      ...it,
      productItemId: it.productItemId ?? null,
      libraryItemId: it.libraryItemId ?? null,
      tradePrice: toNum(it.tradePrice),
      shipping: toNum(it.shipping),
      tariff: toNum(it.tariff),
      prepaidTax: toNum(it.prepaidTax),
      clientUnit: toNum(it.clientUnit),
      deposit: toNum(it.deposit),
      qty: toNum(it.qty),
      source: it.source || 'invoice',
      markup: it.markup || defaultMarkupForItem(it),
      shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
      tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
      room: it.room || '',
      shippingDestination: it.shippingDestination || 'designer_warehouse',
      clientApproved: it.clientApproved === true ? true : it.clientApproved === false ? false : null,
      updatedAt: it.updatedAt || null,
    }))
  );

  const removedIds = useRef([]);

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.itemId === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    removedIds.current.push(id);
    setItems((prev) => prev.filter((it) => it.itemId !== id));
  };

  const genId = () => `itm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const addItemFromCatalog = (it) => {
    setItems((prev) => [
      ...prev,
      {
        ...it,
        itemId: genId(),
        productItemId: it.itemId || it.productItemId || null,
        libraryItemId: null,
        vendorName: it.vendorName || it.vendor || '',
        vendor: it.vendorName || it.vendor || '',
        tradePrice: toNum(it.tradePrice),
        unitShipping: toNum(it.shipping),
        unitTariff: toNum(it.tariff),
        shipping: toNum(it.shipping),
        tariff: toNum(it.tariff),
        prepaidTax: 0,
        deposit: 0,
        qty: 1,
        clientUnit: toNum(it.clientUnit),
        markup: it.markup || defaultMarkupForItem(it),
        shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
        tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
        room: '',
        shippingDestination: defaultShippingDestination,
        clientApproved: null,
      },
    ]);
  };

  /* ----------------------------------------------------
     ROOMS
  ----------------------------------------------------- */

  const [rooms, setRooms] = useState([]);

  const handleRoomCreated = (newRoom) => {
    setRooms((prev) => {
      const exists = prev.some((r) => r.sectionId === newRoom.sk);
      if (exists) return prev;
      return [...prev, { ...newRoom, sectionId: newRoom.sectionId || newRoom.sk }];
    });
  };

  async function refreshRooms() {
    if (!userId) return;

    // ?sectionsOnly=true skips per-item usage hydration (N+1 queries) since we only need sections
    const url = form.projectId
      ? `${API_BASE}/api/library/${userId}/${form.projectId}?sectionsOnly=true`
      : `${API_BASE}/api/library/${userId}`;

    try {
      const res = await fetch(url, { cache: 'no-store' });
      const data = await res.json();
      setRooms(
        sortRoomsForDesignFlow(
          (data.sections || [])
            .filter((s) => s.type === 'section')
            .map((s) => ({ ...s, sectionId: s.sectionId || s.sk }))
        )
      );
    } catch {}
  }

  useEffect(() => {
    refreshRooms();
  }, [userId, form.projectId]);

  useEffect(() => {
    if (!userId || !form.projectId) return undefined;

    const intervalId = window.setInterval(() => {
      refreshRooms();
    }, 30000);

    const handleFocus = () => {
      refreshRooms();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId, form.projectId]);

  /* ----------------------------------------------------
     SCHEDULES
  ----------------------------------------------------- */

  const [schedules, setSchedules] = useState([]);

  const handleScheduleCreated = (newSchedule) => {
    setSchedules((prev) => {
      const exists = prev.some((s) => s.sk === newSchedule.sk);
      if (exists) return prev;
      return [...prev, newSchedule].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  useEffect(() => {
    if (!userId || !form.projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(form.projectId)}`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        setSchedules(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch schedules:', err);
      }
    })();
  }, [userId, form.projectId]);

  /* ----------------------------------------------------
     CATALOG
  ----------------------------------------------------- */

  const [catalog, setCatalog] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [q, setQ] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);

  const refreshCatalog = async () => {
    try {
      setCatalogLoading(true);
      const res = await fetch(`${API_BASE}/api/products/${userId}`, { cache: 'no-store' });
      const data = await res.json();
      setCatalog(
        (Array.isArray(data) ? data : data.Items || data.items || []).map((it) => ({
          itemId: it.itemId || it.id || genId(),
          name: it.name || '',
          description: it.description || '',
          imageUrl: it.imageUrl || '',
          vendorName: it.vendorName || it.vendor || '',
          salesCode: it.salesCode || '',
          type: it.type || (it.category === 'Service' ? 'service' : 'product'),
          tradePrice: toNum(it.tradePrice ?? it.price ?? 0),
          qty: 1,
          unitType: 'Each',
          shipping: toNum(it.shipping ?? 0),
          tariff: toNum(it.tariff ?? 0),
          prepaidTax: 0,
          markup: getCatalogMarkup(it, defaultMarkupForItem(it)),
          shippingMarkup: { type: '%', value: projectDefaultShippingMarkup },
          tariffMarkup: { type: '%', value: 0 },
          deposit: 0,
          taxable: true,
          shippingTaxable: false,
          tariffTaxable: false,
        }))
      );
    } catch (e) {
      setCatalogError(e.message);
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    refreshCatalog();
  }, []);

  /* ----------------------------------------------------
     COMPUTED ITEMS
  ----------------------------------------------------- */

  const computedItems = useMemo(() => {
    return items.map((it) => {
      const trade = toNum(it.tradePrice);
      const qty = toNum(it.qty);

      const computedSell =
        it.markup?.type === '%'
          ? trade + trade * (Number(it.markup.value) / 100)
          : trade + Number(it.markup.value);

      const clientUnitRaw =
        it.lastEdited === 'sell' && Number.isFinite(Number(it.clientUnit))
          ? Number(it.clientUnit)
          : computedSell;
      const clientUnit = round2(Math.max(clientUnitRaw, 0));

      const ship = toNum(it.shipping);
      const shipMarkup =
        it.shippingMarkup?.type === '%'
          ? ship * (Number(it.shippingMarkup.value) / 100)
          : Number(it.shippingMarkup.value);

      const clientShip = round2(ship + shipMarkup);

      const clientTariff = round2(toNum(it.tariff));
      const lineMerch = round2(clientUnit * qty);
      const lineTotal = round2(lineMerch + clientShip + clientTariff);

      return {
        ...it,
        clientUnit,
        clientShipping: clientShip,
        clientTariff,
        lineMerch,
        lineTotal,
      };
    });
  }, [items]);

  /* ----------------------------------------------------
     TOTALS
  ----------------------------------------------------- */

  const totals = useMemo(() => {
    const subtotal = computedItems.reduce((s, it) => s + toNum(it.lineTotal), 0);
    const taxableMerch = computedItems
      .filter((it) => it.taxable)
      .reduce((s, it) => s + it.lineMerch, 0);
    const taxableShipping = computedItems
      .filter((it) => it.shippingTaxable && toNum(it.qty) > 0)
      .reduce((s, it) => s + it.clientShipping, 0);
    const taxableTariff = computedItems
      .filter((it) => it.tariffTaxable && toNum(it.qty) > 0)
      .reduce((s, it) => s + it.clientTariff, 0);
    const tax = round2((taxableMerch + taxableShipping + taxableTariff) * toNum(form.salesTaxRate));
    const total = round2(subtotal + tax);

    return {
      subtotal: round2(subtotal),
      tax,
      total,
      depositRequested: computeDepositRequestAmount(form.depositRequest, total),
      paid: 0,
    };
  }, [computedItems, form.salesTaxRate, form.depositRequest]);

  /* ----------------------------------------------------
     TAGS
  ----------------------------------------------------- */

  const [tags, setTags] = useState(initial?.tags || []);

  useEffect(() => {
    if (!initial || initializedRef.current) return;
    initializedRef.current = true;

    setSavedInvoice(initial);
    setInvoiceStatus(initial.status || 'draft');
    if (initial.invoiceNumber) setInvoiceNumber(initial.invoiceNumber);
    setTags(initial.tags || []);
    setForm((prev) => ({
      ...prev,
      client: initial.client || prev.client,
      projectId: initial.projectId || prev.projectId,
      projectSite: initial.projectSite || prev.projectSite,
      billingAddress: initial.billingAddress || prev.billingAddress,
      salesTaxRate: initial.salesTaxRate != null ? toNum(initial.salesTaxRate) : prev.salesTaxRate,
      notes: initial.notes || prev.notes,
      attachments: initial.attachments || prev.attachments,
      invoiceDate: initial.invoiceDate || prev.invoiceDate,
      dueDate: initial.dueDate || prev.dueDate,
      title: initial.title ?? prev.title,
      depositRequest: toDepositRequestForm(initial.depositRequest),
    }));
    setItems(
      (initial.lineItems || []).map((it) => ({
        ...it,
        productItemId: it.productItemId ?? null,
        libraryItemId: it.libraryItemId ?? null,
        tradePrice: toNum(it.tradePrice),
        shipping: toNum(it.shipping),
        tariff: toNum(it.tariff),
        prepaidTax: toNum(it.prepaidTax),
        clientUnit: toNum(it.clientUnit),
        deposit: toNum(it.deposit),
        qty: toNum(it.qty),
        source: it.source || 'invoice',
        markup: it.markup || defaultMarkupForItem(it),
        shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
        tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
        room: it.room || '',
        shippingDestination: it.shippingDestination || 'designer_warehouse',
        clientApproved: it.clientApproved === true ? true : it.clientApproved === false ? false : null,
        updatedAt: it.updatedAt || null,
      }))
    );
  }, [initial]);

  /* ----------------------------------------------------
     SAVE INVOICE
  ----------------------------------------------------- */

  const isLocked = String(invoiceStatus || '').toLowerCase() === 'paid';

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const saveInFlightRef = useRef(false);

  const existingHistory = savedInvoice?.paymentHistory ?? initial?.paymentHistory ?? [];
  const paidFromHistory = (Array.isArray(existingHistory) ? existingHistory : []).reduce(
    (sum, p) => sum + toNum(p?.amount),
    0
  );
  const paidFromTotals = toNum(savedInvoice?.totals?.paid ?? initial?.totals?.paid ?? 0);
  const existingPaid = Math.max(paidFromTotals, paidFromHistory);

  const buildPayload = () => ({
    userId,
    invoiceNumber,
    title: form.title,
    companyName: project?.companyName,
    projectName: project?.projectNickname || project?.name,
    projectSite: form.projectSite,
    billingAddress: form.billingAddress,
    shippingAddress: form.projectSite,
    client: form.client,
    projectId: form.projectId,
    salesTaxRate: toNum(form.salesTaxRate),
    notes: form.notes,
    attachments: form.attachments,
    invoiceDate: form.invoiceDate,
    dueDate: form.dueDate || null,
    depositRequest:
      toNum(form.depositRequest.value) > 0
        ? { type: form.depositRequest.type, value: toNum(form.depositRequest.value) }
        : null,
    tags,
    lineItems: computedItems,
    tax: {
      rate: toNum(form.salesTaxRate),
      locationId:
        savedInvoice?.tax?.locationId ??
        initial?.tax?.locationId ??
        resolvedTaxLocation?.id ??
        project?.salesTaxRate ??
        null,
      location:
        resolvedTaxLocation ?? savedInvoice?.tax?.location ?? initial?.tax?.location ?? null,
    },
    totals: { ...totals, paid: existingPaid },
    remainingBalance: Math.max((totals.total || 0) - existingPaid, 0),
    paymentHistory: existingHistory,
    status: invoiceStatus || savedInvoice?.status || initial?.status || 'draft',
    shareToken: savedInvoice?.shareToken ?? initial?.shareToken,
  });

  async function saveDraft(stayOpen = false, options = {}) {
    if (saveInFlightRef.current) return null;
    if (isLocked) return setError('This invoice is paid and locked. Change the status to edit.');
    if (!form.client.email) return setError('Client email required.');
    if (items.length === 0) return setError('Add at least one item.');
    if (form.depositRequest.type === 'percent' && toNum(form.depositRequest.value) > 100) {
      return setError('Deposit percentage cannot exceed 100%.');
    }
    if (form.depositRequest.type === 'amount' && toNum(form.depositRequest.value) > totals.total) {
      return setError('Deposit amount cannot exceed the invoice total.');
    }

    const existingInvoiceId = savedInvoice?.invoiceId || initial?.invoiceId;

    saveInFlightRef.current = true;
    setSaving(true);
    setError(null);

    const nextStatus = options?.status || invoiceStatus || savedInvoice?.status || initial?.status || 'draft';

    try {
      const url = existingInvoiceId
        ? `${API_BASE}/api/invoices/${userId}/${form.projectId}/${existingInvoiceId}`
        : `${API_BASE}/api/invoices`;

      const payload = {
        ...buildPayload(),
        status: nextStatus,
      };
      if (nextStatus === 'sent') {
        payload.sentAt = new Date().toISOString();
      }

      const res = await fetch(url, {
        method: existingInvoiceId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('Invoice save failed:', text);
        throw new Error(text || 'Failed to save invoice.');
      }

      const saved = await res.json();

      setInvoiceNumber(saved.invoiceNumber);
      setSavedInvoice(saved);
      setInvoiceStatus(saved.status || nextStatus || 'draft');
      if (Array.isArray(saved?.lineItems)) {
        setItems(
          saved.lineItems.map((it) => ({
            ...it,
            productItemId: it.productItemId ?? null,
            libraryItemId: it.libraryItemId ?? null,
            tradePrice: toNum(it.tradePrice),
            shipping: toNum(it.shipping),
            tariff: toNum(it.tariff),
            prepaidTax: toNum(it.prepaidTax),
            clientUnit: toNum(it.clientUnit),
            deposit: toNum(it.deposit),
            qty: toNum(it.qty),
            source: it.source || 'invoice',
            markup: it.markup || defaultMarkupForItem(it),
            shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
            tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
            room: it.room || '',
            shippingDestination: it.shippingDestination || 'designer_warehouse',
            clientApproved: it.clientApproved === true ? true : it.clientApproved === false ? false : null,
            updatedAt: it.updatedAt || null,
          }))
        );
      }

      let finalSaved = saved;

      const qbShouldSync =
        qbConnected &&
        shouldAutoCreateInQuickBooks({ quickbooks: quickbooksSettings }) &&
        saved?.invoiceId &&
        form.projectId;

      if (qbShouldSync) {
        const qbSync = saved?.quickbooks?.invoiceId
          ? autoUpdateQuickBooksInvoice({ invoiceId: saved.invoiceId, projectId: form.projectId })
          : autoCreateQuickBooksInvoice({ invoiceId: saved.invoiceId, projectId: form.projectId });

        const qbRefreshUrl = `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(saved.invoiceId)}`;

        if (stayOpen) {
          // fire-and-forget when staying on form — updates QB link once sync completes
          qbSync
            .then((qbResult) => {
              if (qbResult?.skipped) notify.warning(qbResult.warning, { duration: 12000 });
              return fetch(qbRefreshUrl, { cache: 'no-store' });
            })
            .then((r) => (r.ok ? r.json() : null))
            .then((refreshed) => { if (refreshed) setSavedInvoice(refreshed); })
            .catch((qbErr) => {
              setError(
                `Invoice saved, but QuickBooks auto-sync failed: ${
                  qbErr.message || 'Unable to sync invoice.'
                }`
              );
            });
        } else {
          // await sync before navigating so the view page loads with the QB link already showing
          try {
            const qbResult = await qbSync;
            if (qbResult?.skipped) notify.warning(qbResult.warning, { duration: 12000 });
            const r = await fetch(qbRefreshUrl, { cache: 'no-store' });
            if (r.ok) {
              finalSaved = await r.json();
              setSavedInvoice(finalSaved);
            }
          } catch {
            // QB sync failed — navigate without QB ID, user can sync manually
          }
        }
      }

      if (stayOpen) refreshRooms();

      // 🔑 Redirect only when closing
      if (!stayOpen) {
        if (onSaved) {
          try {
            onSaved(finalSaved);
          } catch (err) {
            console.error('onSaved failed:', err);
          }
        }

        const safeProjectId =
          form.projectId || project?.projectId || project?.id || project?.projectId;

        if (safeProjectId) {
          navigate(`/projects/${safeProjectId}/invoices`, {
            state: { invoice: finalSaved, project },
          });
        } else {
          navigate(-1);
        }
      }

      return finalSaved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
      saveInFlightRef.current = false;
    }
  }

  const emailClient = async () => {
    const saved = await saveDraft(true, { status: 'sent' });
    if (!saved?.invoiceId) return setError("We couldn't save this document. Please try again.");
    const publicUrl = buildPublicInvoiceUrl(window.location.origin, saved.shareToken);
    const primaryEmail = form.client?.email || project?.email || '';
    setSendEmailModal({
      open: true,
      email: [...new Set([primaryEmail, project?.secondaryEmail].filter(Boolean))].join(', '),
      publicUrl,
      projectId: saved.projectId || form.projectId || '',
      invoiceId: saved.invoiceId,
    });
  };

  const convertToPO = () => {
    const invoiceId = savedInvoice?.invoiceId;
    if (!invoiceId || !userId || !form.projectId) {
      notify('Please save the invoice before converting to a purchase order.');
      return;
    }
    if (items.length === 0) {
      notify('This invoice has no line items to convert.');
      return;
    }
    setItemSelectModal({ open: true, selected: items.map((it) => it.itemId).filter(Boolean) });
  };

  const toggleConvertItemSelected = (itemId) => {
    setItemSelectModal((s) => ({
      ...s,
      selected: s.selected.includes(itemId)
        ? s.selected.filter((id) => id !== itemId)
        : [...s.selected, itemId],
    }));
  };

  const toggleConvertAllItemsSelected = () => {
    setItemSelectModal((s) => ({
      ...s,
      selected: s.selected.length === items.length ? [] : items.map((it) => it.itemId).filter(Boolean),
    }));
  };

  const proceedFromItemSelection = async () => {
    const invoiceId = savedInvoice?.invoiceId;
    const itemIds = itemSelectModal.selected;
    if (!invoiceId || itemIds.length === 0) {
      notify('Select at least one item to convert.');
      return;
    }
    setItemSelectModal({ open: false, selected: [] });
    setPendingConvertItemIds(itemIds);

    try {
      setConvertingPO(true);
      const res = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(invoiceId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dryRun: true, itemIds }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to check vendors.');
        return;
      }

      setUnassignedCount(data.unassignedCount || 0);
      if (Array.isArray(data.vendors) && data.vendors.length > 1) {
        setVendorModal({ open: true, vendors: data.vendors });
      } else {
        setConfirmPOModal(true);
      }
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to convert to purchase order.');
    } finally {
      setConvertingPO(false);
    }
  };

  const runConvertToPO = async (splitByVendor) => {
    const invoiceId = savedInvoice?.invoiceId;
    if (!invoiceId || !userId || !form.projectId) return;
    const itemIds = pendingConvertItemIds;

    try {
      setConvertingPO(true);
      const res = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(invoiceId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ splitByVendor, itemIds }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to convert to purchase order.');
        return;
      }
      const poIds = Array.isArray(data.poIds) && data.poIds.length ? data.poIds : [data.openedPoId || data.poId].filter(Boolean);
      const poId = data.openedPoId || poIds[0];
      if (poId) {
        const state = { project };
        if (poIds.length > 1) {
          // Multiple draft POs were created (split by vendor) — queue the rest so
          // "Save & Close" on this one advances straight into the next instead of
          // dropping the user back on the invoice after only the first PO.
          state.poQueue = poIds.filter((id) => id !== poId);
          state.poQueueTotal = poIds.length;
          state.poQueuePosition = 1;
        }
        navigate(`/projects/${form.projectId}/purchase-orders/${poId}`, { state });
      }
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to convert to purchase order.');
    } finally {
      setConvertingPO(false);
      setConfirmPOModal(false);
      setVendorModal({ open: false, vendors: [] });
      setPendingConvertItemIds([]);
    }
  };

  const refreshInvoice = async () => {
    if (!savedInvoice?.invoiceId || !form.projectId || !userId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(savedInvoice.invoiceId)}`,
        { cache: 'no-store' }
      );
      if (!res.ok) return;
      const data = await res.json();
      setSavedInvoice(data);
      setInvoiceStatus(data?.status || invoiceStatus);
    } catch {}
  };

  const downloadPDF = async () => {
    if (!pdfRef.current) return;
    const el = pdfRef.current;
    const imgs = Array.from(el.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    );
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `Invoice-${invoiceNumber || savedInvoice?.invoiceId || 'invoice'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(el)
      .save();
  };

  /* ----------------------------------------------------
     RENDER
  ----------------------------------------------------- */

  return (
    <div className="doc-page">
      <div className="invoice-pdf-export" aria-hidden="true">
        <div ref={pdfRef}>
          <InvoicePdfDocument
            invoice={{
              ...buildPayload(),
              invoiceId: savedInvoice?.invoiceId || initial?.invoiceId,
              invoiceNumber,
              createdAt: savedInvoice?.createdAt || initial?.createdAt,
              updatedAt: savedInvoice?.updatedAt || initial?.updatedAt,
            }}
            company={companySettings}
            apiBase={API_BASE}
            template={invoiceTemplate}
          />
        </div>
      </div>

      <div>
        <HeaderSection proposalNumber={invoiceNumber} title={form.title} onClose={onClose} docType="invoice" />

        <div className="if-card" style={{ marginTop: 12 }}>
          <div className="po-grid four">
            <div className="po-field">
              <label>Name (optional)</label>
              <input
                className="if-input"
                type="text"
                value={form.title}
                maxLength={DOCUMENT_TITLE_MAX_LENGTH}
                placeholder="e.g. Living Room"
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div className="po-field">
              <label>Status</label>
              <select
                className="if-select"
                value={invoiceStatus}
                disabled={!canEdit}
                onChange={(e) => setInvoiceStatus(e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="overdue">Overdue</option>
                <option value="partial">Partially Paid</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            <div className="po-field">
              <label>Date</label>
              <input
                className="if-input"
                type="date"
                value={form.invoiceDate ? String(form.invoiceDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, invoiceDate: e.target.value }))}
              />
            </div>

            <div className="po-field">
              <label>Due Date</label>
              <input
                className="if-input"
                type="date"
                value={form.dueDate ? String(form.dueDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </div>

            <div className="po-field">
              <label>Deposit Requested</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  className="if-select"
                  style={{ width: 64, flexShrink: 0 }}
                  value={form.depositRequest.type}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, depositRequest: { ...f.depositRequest, type: e.target.value } }))
                  }
                >
                  <option value="percent">%</option>
                  <option value="amount">$</option>
                </select>
                <input
                  className="if-input"
                  type="number"
                  min="0"
                  max={form.depositRequest.type === 'percent' ? 100 : undefined}
                  step="0.01"
                  placeholder="None"
                  value={form.depositRequest.value}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, depositRequest: { ...f.depositRequest, value: e.target.value } }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <InfoSection
          form={form}
          setForm={setForm}
          editHeader={editHeader}
          setEditHeader={setEditHeader}
          project={project}
          readOnly={!canEdit}
        />

        {isLocked && (
          <div className="proposal-lock-cover" style={{ position: 'relative', margin: '12px 0' }}>
            <div className="proposal-lock-cover-msg">
              This invoice is marked Paid and is locked. Change the status to edit it.
            </div>
          </div>
        )}

        <div className={`proposal-editable ${isLocked ? 'locked' : ''}`}>
          <div className="proposal-editable-content">
            <div className="line-items-section">
              {computedItems.map((it) => (
                <LineItemCard
                  key={it.itemId}
                  it={it}
                  updateItem={updateItem}
                  removeItem={removeItem}
                  rooms={rooms}
                  schedules={schedules}
                  onScheduleCreated={handleScheduleCreated}
                  userId={userId}
                  projectId={form.projectId}
                  invoiceId={savedInvoice?.invoiceId || initial?.invoiceId}
                  invoiceNumber={invoiceNumber}
                  onRoomCreated={handleRoomCreated}
                  hideDeposit
                  hideAlternatives
                  freightDestinations={freightDestinations}
                  readOnly={!canEdit}
                />
              ))}
            </div>

            {canEdit && (
              <CatalogSection
                catalog={catalog}
                q={q}
                setQ={setQ}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                catalogLoading={catalogLoading}
                catalogError={catalogError}
                addItemFromCatalog={addItemFromCatalog}
                setShowAddItemModal={setShowAddItemModal}
                fmtUSD={fmt2}
              />
            )}

            <MemoTagsSection
              form={form}
              setForm={setForm}
              tags={tags}
              setTags={setTags}
              readOnly={!canEdit}
            />

            <AttachmentsSection form={form} setForm={setForm} readOnly={!canEdit} />

            <TotalsSection
              totals={{ ...totals, depositDue: totals.depositRequested }}
              fmtUSD={fmt2}
              paid={existingPaid}
              amountDue={Math.max((totals.total || 0) - existingPaid, 0)}
              showDepositDue={totals.depositRequested > 0}
              depositPct={form.depositRequest.type === 'percent' ? form.depositRequest.value : null}
            />
          </div>
        </div>

        {savedInvoice?.invoiceId && (
          <div ref={paymentsSectionRef}>
            <InvoicePayments
              invoice={savedInvoice}
              projectId={form.projectId}
              onUpdated={refreshInvoice}
            />
          </div>
        )}

      </div>

      <InvoiceActions
        onClose={onClose}
        saveDraft={saveDraft}
        emailClient={emailClient}
        onDownloadPDF={downloadPDF}
        onConvertToPO={convertToPO}
        convertingPO={convertingPO}
        saving={saving}
        invoiceId={savedInvoice?.invoiceId}
        projectId={form.projectId}
        quickbooks={savedInvoice?.quickbooks}
        canEdit={canEdit}
      />

      {itemSelectModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setItemSelectModal({ open: false, selected: [] })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select items to convert</h3>
            <p>Choose which line items from this invoice should be included on the new purchase order.</p>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={itemSelectModal.selected.length === items.length && items.length > 0}
                  onChange={toggleConvertAllItemsSelected}
                />
                Select All ({itemSelectModal.selected.length}/{items.length})
              </label>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px' }}>
              {items.map((it) => (
                <label
                  key={it.itemId}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '8px 0',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={itemSelectModal.selected.includes(it.itemId)}
                    onChange={() => toggleConvertItemSelected(it.itemId)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontWeight: 500 }}>{it.name || 'Untitled item'}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>
                      Qty {Number(it.qty || 0)} &middot; {Number(it.tradePrice || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                      {it.room ? ` · ${it.room}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setItemSelectModal({ open: false, selected: [] })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={proceedFromItemSelection}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmPOModal}
        title="Create Purchase Order"
        message={`Create a purchase order from ${pendingConvertItemIds.length} selected item${
          pendingConvertItemIds.length === 1 ? '' : 's'
        }? A new draft PO will be generated from the selected line items.${unassignedItemsNotice(
          unassignedCount
        )}`}
        confirmLabel="Create PO"
        onConfirm={() => runConvertToPO(false)}
        onCancel={() => setConfirmPOModal(false)}
      />

      {vendorModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setVendorModal({ open: false, vendors: [] })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Multiple vendors found</h3>
            <p>
              This invoice includes items from multiple vendors. Create one purchase order per
              vendor?
              {unassignedItemsNotice(unassignedCount)}
            </p>
            <div style={{ marginBottom: 10 }}>
              {vendorModal.vendors.map((v) => (
                <span
                  key={v}
                  style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: '#eef2ff',
                    color: '#1e1b4b',
                    fontSize: 12,
                    marginRight: 6,
                    marginBottom: 6,
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setVendorModal({ open: false, vendors: [] })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={() => runConvertToPO(true)}>
                Create Purchase Orders
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="po-error">{error}</div>}

      {showAddItemModal && (
        <div className="modal-backdrop">
          <div className="proposal-modal-content">
            <AddItemForm
              onClose={() => {
                setShowAddItemModal(false);
                refreshCatalog();
              }}
              refreshItems={refreshCatalog}
            />
          </div>
        </div>
      )}

      {sendEmailModal.open && (
        <SendEmailModal
          type="invoice"
          defaultEmail={sendEmailModal.email}
          clientName={form.client?.name || ''}
          docNumber={invoiceNumber}
          docUrl={sendEmailModal.publicUrl}
          companyName={form.companyName || project?.companyName || ''}
          projectId={sendEmailModal.projectId}
          documentId={sendEmailModal.invoiceId}
          onClose={() => setSendEmailModal({ open: false, email: '', publicUrl: '', projectId: '', invoiceId: '' })}
          onSent={() => notify('Invoice sent to client.')}
        />
      )}
    </div>
  );
}

InvoiceForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  project: PropTypes.object,
  initial: PropTypes.object,
};

